import React, { FC, useState } from 'react';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { styled, Theme, CSSObject } from '@mui/material/styles';

import { Link } from 'gatsby';

import PaymentsIcon from '@mui/icons-material/Payments';
import MuiDrawer from '@mui/material/Drawer';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  List,
  Divider,
  Collapse,
} from '@mui/material';

import { useLocationProductWarnCountSubscription } from 'generated/graphql';

import { useAuthContext } from 'components/authorization/AuthProvider';
import { Role_Enum } from 'generated/graphql';
import {
  ExpandLess,
  ExpandMore,
  Settings,
  FilePresent,
} from '@mui/icons-material';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  paddingTop: theme.mixins.toolbar.minHeight,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  paddingTop: theme.mixins.toolbar.minHeight,
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface props {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

export const LeftDrawer: FC<props> = ({ isOpen, setIsOpen }) => {
  const { authUser } = useAuthContext();
  const [msoSettingsOpen, setMsoSettingsOpen] = useState<boolean>(false);

  const { data: warningCount } = useLocationProductWarnCountSubscription({
    //skip: !authUser?.selectedLocation,
    variables: { location: authUser?.selectedLocation?.id },
  });

  const msoSettingsItems = {
    rooms: [
      {
        text: 'Rooms',
        icon: <i className="fak fa-rooms"></i>,
        link: '/app/rooms',
      },
      {
        text: 'Room Types',
        icon: <i className="fak fa-room-type"></i>,
        link: '/app/room-type',
      },
      {
        text: 'Room Model Assignments',
        icon: <i className="fak fa-device-room-assignment"></i>,
        link: '/app/room-model',
      },
      {
        text: 'Device Count',
        icon: <i className="fak fa-device-count"></i>,
        link: '/app/model-count',
      },
    ],
    location: [
      {
        text: 'Open Hours',
        icon: <i className="fak fa-open-hours"></i>,
        link: '/app/openhours',
      },
      {
        text: 'Location Dashboard',
        icon: <i className="fak fa-reports-v1"></i>,
        link: '/app/mso-dashboard',
      },
      {
        text: 'Accreditation Settings',
        icon: <i className="fak fa-accreditation"></i>,
        link: '/app/accreditation-settings',
      },
    ],
    financial: [
      {
        text: 'Tax Rates',
        icon: <i className="fak fa-tax-rates"></i>,
        link: '/app/tax-rates',
      },
      {
        text: 'Plans',
        icon: <i className="fak fa-plans"></i>,
        link: '/app/plans',
      },
      {
        text: 'Volume Discounts',
        icon: <i className="fak fa-volume-discount"></i>,
        link: '/app/volume-discount',
      },
      {
        text: 'Invoice Financial Reports',
        icon: <FilePresent />,
        link: '/app/invoice-financial-reports',
      },
    ],
    globalItems: [
      {
        text: 'Product Types',
        icon: <i className="fak fa-global-products"></i>,
        link: '/app/product-type',
      },
      {
        text: 'Global Products',
        icon: <i className="fak fa-global-products"></i>,
        link: '/app/products',
      },
      {
        text: 'Device',
        icon: <i className="fak fa-device"></i>,
        link: '/app/model',
      },
      {
        text: 'Locations',
        icon: <i className="fak fa-locations"></i>,
        link: '/app/locations',
      },
      {
        text: 'Direct Purchase Orders',
        icon: <i className="fak fa-global-products"></i>,
        link: '/app/direct-purchase-orders',
      },
    ],
    settings: [
      {
        text: 'MSO Settings',
        icon: <i className="fak fa-mso-settings"></i>,
        link: '/app/mso-settings',
      },
    ],
  };

  // tabs for just vansanity admins
  if (authUser.role === Role_Enum.VansanityAdmin) {
    msoSettingsItems.globalItems.push({
      text: 'Global Procedures',
      icon: <i className="fak fa-device"></i>,
      link: '/app/procedures',
    });
    msoSettingsItems.globalItems.push({
      text: 'Vansanity Seed Templates',
      icon: <FilePresent />,
      link: '/app/signing-templates',
    });
  }

  // tabs for location admins and above
  if (
    [Role_Enum.VansanityAdmin, Role_Enum.LocationAdmin].includes(authUser.role)
  ) {
    msoSettingsItems.location.push({
      text: 'Location Procedures',
      icon: <i className="fak fa-rooms"></i>,
      link: '/app/location-procedures',
    });
  }

  const topLevelItems = [
    {
      text: 'Bookings',
      icon: <i className="fak fa-bookings"></i>,
      link: '/app/bookings',
    },
    {
      text: 'Users',
      icon: <i className="fak fa-users"></i>,
      link: '/app/users',
    },
    {
      text: 'Products',
      icon: <i className="fak fa-product-at-location"></i>,
      link: '/app/location-products',
      warning:
        warningCount?.location_product_aggregate?.aggregate.count > 0 ??
        'false',
    },
    {
      text: 'MSO Purchases',
      icon: <i className="fak fa-plans"></i>,
      link: '/app/purchase-requests',
    },
    {
      text: 'Payments',
      icon: <PaymentsIcon />,
      link: '/app/payments',
    },
    {
      text: 'Business Analytics',
      icon: <AnalyticsIcon />,
      link: '/app/mso-revenue',
    },
  ];

  const adminAuth =
    authUser?.authorized &&
    [
      Role_Enum.VansanityAdmin,
      Role_Enum.LocationAdmin,
      Role_Enum.Manager,
      Role_Enum.ReadOnlyAdmin,
      Role_Enum.ProductOnlyLocationAdmin,
    ].includes(authUser.role);

  return (
    <Drawer variant="permanent" open={isOpen}>
      {adminAuth && (
        <>
          <List>
            {topLevelItems.map(({ text, icon, link, warning }) => (
              <ListItem
                button
                key={link}
                component={Link}
                to={link}
                sx={{ marginBottom: '6px' }}
              >
                <Tooltip title={text}>
                  <ListItemIcon
                    sx={{
                      fontSize: '20px',
                      ...(warning ? { color: 'red' } : {}),
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                </Tooltip>
                {isOpen && <ListItemText primary={text} />}
              </ListItem>
            ))}
            <ListItem
              button
              sx={{ marginBottom: -1 }}
              onClick={() => {
                setMsoSettingsOpen((value) => (isOpen ? !value : true));
              }}
            >
              <Tooltip title="Settings">
                <ListItemIcon
                  onClick={() => setIsOpen(true)}
                  sx={{
                    fontSize: '20px',
                  }}
                >
                  <Settings />
                </ListItemIcon>
              </Tooltip>
              {isOpen && <ListItemText primary="MSO Settings" />}
              {isOpen &&
                (msoSettingsOpen ? (
                  <ExpandLess htmlColor="#888" />
                ) : (
                  <ExpandMore htmlColor="#888" />
                ))}
            </ListItem>
          </List>
          <List sx={{ overflowY: 'auto' }}>
            <Collapse in={isOpen && msoSettingsOpen} sx={{ marginTop: -1 }}>
              {Object.entries(msoSettingsItems).map(([category, items], i) => (
                <div key={category}>
                  {i !== 0 && <Divider />}
                  {items.map(({ link, text }) => (
                    <ListItem
                      button
                      key={link}
                      component={Link}
                      to={link}
                      sx={{ marginBottom: '6px' }}
                    >
                      {isOpen && <ListItemText secondary={text} />}
                    </ListItem>
                  ))}
                </div>
              ))}
            </Collapse>
          </List>
        </>
      )}
    </Drawer>
  );
};
